<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                element="InertiaLink"
                :pages="[
                    {
                        name: $tc('messages.fouls', 2),
                        href: meta.links.index,
                    },
                    {
                        name: student.full_name,
                        href: null,
                    },
                ]"
            />
        </template>

        <div class="md:grid md:grid-cols-3 md:gap-6">
            <div class="mt-5 md:mt-0 bg-gray-50">
                <AppWell>
                    <AppCard>
                        <InertiaLink :href="meta.links.student_show">
                            <img
                                :src="student.avatar_or_default"
                                class="mx-auto rounded-full h-32 w-32 border-4 border-white"
                            />
                        </InertiaLink>

                        <div class="bg-white px-4 py-5 sm:px-6">
                            <div class="flex flex-col justify-center items-center">
                                <h3 class="text-lg leading-6 font-medium text-gray-900">
                                    <InertiaLink :href="meta.links.student_show" class="text-indigo-500">
                                        {{ student.name_or_nickname }}
                                    </InertiaLink>
                                </h3>
                                <h3 class="leading-6 font-base text-gray-600">
                                    {{ $t('messages.student') }}
                                </h3>
                            </div>
                        </div>
                    </AppCard>
                    <AppCard class="mt-3">
                        <div class="flex gap-2 flex-wrap">
                            <AppConfirmActionButton
                                :href="meta.links.delete"
                                :button-text="$t('messages.delete')"
                                v-if="meta.can.delete"
                                danger
                                :confirm-text="$t('messages.delete')"
                            />
                            <AppButton element="InertiaLink" :href="meta.links.edit" v-if="meta.can.edit">
                                {{ $t('messages.edit') }}
                            </AppButton>
                            <AppButton :href="meta.links.foul_print">
                                {{ $t('messages.pdf') }}
                            </AppButton>
                            <AppButton element="InertiaLink" :href="meta.links.student_foul_summary">
                                {{ $t('messages.fouls_summary') }}
                            </AppButton>
                        </div>
                    </AppCard>
                    <AppCard class="mt-3" v-if="foul.teacher_name">
                        <label class="block text-sm font-medium leading-5 text-gray-700">
                            {{ $tc('messages.teachers', 1) }}
                        </label>
                        <div class="mt-1">
                            {{ foul.teacher_name }}
                        </div>
                    </AppCard>
                </AppWell>
            </div>

            <div class="mt-5 md:mt-0 md:col-span-2">
                <div class="md:grid md:grid-cols-6 md:gap-6">
                    <div class="sm:col-span-2">
                        <div class="mt-1">
                            <FormLabel :value="$tc('messages.subject', 1)" for="subject_id" />
                            {{ foul.subject_name }}
                        </div>
                    </div>

                    <div class="sm:col-span-2">
                        <div class="mt-1">
                            <FormLabel :value="$tc('messages.places', 1)" for="place" />
                            {{ foul.place }}
                        </div>
                    </div>

                    <div class="sm:col-span-2">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.date')" for="date" />
                            {{ foul.date_display }}
                        </div>
                    </div>

                    <div class="sm:col-span-2">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.time')" for="time" />
                            {{ foul.time }}
                        </div>
                    </div>

                    <div class="sm:col-span-2">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.turn')" for="turn" />
                            <div class="mt-1">
                                {{ foul.turn_display }}
                            </div>
                        </div>
                    </div>

                    <div class="sm:col-span-2">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.type')" for="type" />
                            {{ foul.type_display }}
                        </div>
                    </div>

                    <div class="sm:col-span-2">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.severity')" for="severity" />
                            {{ foul.severity_display }}
                        </div>
                    </div>

                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.internal_teacher_note')" for="internal_teacher_note" />
                            {{ foul.internal_teacher_note }}
                        </div>
                    </div>

                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.public_description')" for="public_description" />
                            {{ foul.public_description }}
                        </div>
                    </div>

                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.punishment')" for="punishment" />
                            {{ foul.punishment }}
                        </div>
                    </div>

                    <div class="sm:col-span-6 mt-3">
                        <label class="block text-sm font-medium leading-5 text-gray-700">
                            {{ $tc('messages.links', 2) }}
                        </label>
                        <div class="mt-1">
                            <div class="sm:col-span-6">
                                <div class="mt-1">
                                    <ul class="list-disc">
                                        <li v-for="link in foul.links" :key="link.id">
                                            <a :href="link.url" class="text-indigo-500">
                                                {{ link.title ?? link.url }}
                                            </a>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </PanelPage>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import FormLabel from '@/components/Forms/FormLabel.vue';
import AppWell from '@/Shared/AppWell.vue';
import AppCard from '@/Shared/AppCard.vue';
import AppButton from '@/components/AppButton.vue';
import AppConfirmActionButton from '@/components/AppConfirmActionButton.vue';

export default {
    components: {
        AppConfirmActionButton,
        AppButton,
        AppCard,
        AppWell,
        PanelPage,
        FormLabel,
    },
    layout: Layout,
    name: 'Show',
    props: ['meta', 'student', 'foul'],
};
</script>
